import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { useVerifyAddress } from "@/api/kyc-query";
import { useCities, useStates } from "@/api/util";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import useAuthStore from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";

import UploadFile from "../../form/file-input";
import { ItemSearch } from "../search";

const formSchema = z.object({
	stateId: z.string().min(1, { message: "State is required" }),
	cityId: z.string().min(1, { message: "City is required" }),
	address: z.string().min(1, { message: "Home address is required" }),
	utilityBill: z.instanceof(FileList).refine(
		files => files.length > 0,
		{ message: "Utility bill is required" }
	).refine(
		(files) => {
			if (files.length === 0)
				return true;
			const file = files[0];
			return ["image/png", "image/jpeg", "image/jpg"].includes(file.type);
		},
		{ message: "File must be PNG, JPEG or JPG" }
	).optional()
});

type FormValues = z.infer<typeof formSchema>;

export default function AddressConfirmation() {
	const [open, setOpen] = useState(false);
	const [files, setFiles] = useState<File[] | null>(null);

	const user = useAuthStore(state => state.user);
	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			stateId: "",
			cityId: "",
			address: ""
		}
	});

	const statesQuery = useStates(user?.countryId ?? "");

	const citiesQuery = useCities({
		countryId: user?.countryId ?? "",
		stateId: form.watch("stateId")
	});

	const verifyAddress = useVerifyAddress();
	async function onSubmit(values: FormValues) {
		const data = await verifyAddress.mutateAsync({
			...values,
			userId: user?.id ?? ""
		});
		if (data)
			setOpen(false);
	}

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			form.reset();
			setFiles(null);
		}
		setOpen(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button variant="link">Add</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md p-0 gap-0">
				<Card className="w-full border-0 shadow-none">
					<CardHeader className="border-b p-4">
						<DialogTitle className="text-xl font-medium text-center">Confirm address</DialogTitle>
						<DialogDescription className="sr-only">Confirm address</DialogDescription>
					</CardHeader>
					<CardContent className="pt-6">
						<Form {...form}>
							<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
								<ItemSearch
									control={form.control}
									name="stateId"
									items={statesQuery.data ?? []}
									isLoading={statesQuery.isPending}
									label="State"
									placeholder="Select state..."
									searchPlaceholder="Search state..."
									emptyMessage="No state found."
								/>

								<ItemSearch
									control={form.control}
									name="cityId"
									items={citiesQuery.data ?? []}
									isLoading={citiesQuery.isPending}
									label="City"
									placeholder="Select city..."
									searchPlaceholder="Search city..."
									emptyMessage="No city found."
									disabled={!form.watch("stateId")}
								/>

								<FormField
									control={form.control}
									name="address"
									render={({ field }) => (
										<FormItem className="space-y-2">
											<FormLabel className="font-normal">
												Home address
												{" "}
												<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Enter home address" className="h-12" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<UploadFile
									files={files}
									setFiles={setFiles}
									className="w-full outline-dashed outline-1 outline-primary hover:bg-primary/20"
									name="utilityBill"
									control={form.control}
								/>

								<div className="flex justify-between pt-4">
									<Button
										type="button"
										variant="outline"
										className="px-8"
										onClick={() => {
											setOpen(false);
											form.reset();
										}}
									>
										Discard
									</Button>
									<Button type="submit" className="px-8">
										Save update
									</Button>
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>
			</DialogContent>
		</Dialog>
	);
}
